import { useState } from 'react';
import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useSSE } from '../hooks/useSSE';
import type { Theme } from '../lib/tokens';
import { FONTS } from '../lib/tokens';
import { Portrait } from '../components/Portrait';
import { Img } from '../components/Img';
import { Icon } from '../components/icons';
import { useAuth } from '../hooks/useAuth';
import { ChatScreen } from './ChatScreen';
import { DateSheet } from '../sheets/DateSheet';
import { DateScheduler } from '../sheets/DateScheduler';
import { CoupleProfileView } from './Discover';
import type { MatchInfo, CoupleFeedCandidate } from './Discover';
import { GraduationPrompt } from '../components/GraduationPrompt';
import { SkeletonMatches } from '../components/Skeletons';
import { GraduationSheet } from '../sheets/GraduationSheet';
import { ReportBlockSheet } from '../sheets/ReportBlockSheet';
import { useScreenView } from '../lib/analytics';
import { hapticLight } from '../lib/haptics';
import { ethnicityLabel } from '../lib/identity';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8080';

interface DateProposal {
  proposedBy: string;
  venueId: string;
  venueName: string;
  date: string;
  time: string;
  partySize: number;
  status: 'pending' | 'confirmed' | 'declined';
}

interface CoupleSnippet {
  coupleId: string;
  user1: { displayName: string; avatarUrl: string | null; photos?: string[] };
  user2: { displayName: string; avatarUrl: string | null; photos?: string[] };
}

interface PartnerPick {
  likeId: string;
  pickedBy: string;
  awaitingName?: string; // couple member who still needs to approve (waiting mode)
  createdAt: string;
  targetCouple: {
    coupleId: string;
    coupleBio: string | null;
    user1: { displayName: string; avatarUrl: string | null; photos: string[]; age?: number };
    user2: { displayName: string; avatarUrl: string | null; photos: string[]; age?: number };
    // Detail fields (optional: tolerate cached responses from older backend)
    sparkScore?: number;
    sparkLabel?: 'ember' | 'flame' | 'blaze';
    compatibilityHint?: string;
    combinedActivities?: string[];
  };
}

interface PicksResponse {
  toReview: PartnerPick[];
  waiting: PartnerPick[];
  sent?: PartnerPick[]; // couples your couple has liked, waiting to hear back (optional: tolerate older backend)
}

// Compact "2d ago" style relative time for sent-like timestamps.
function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return `${Math.floor(days / 7)}w ago`;
}

// One shared status signal used everywhere a card might need action vs not —
// color + position carry the state, so text is confirmation, not the only clue.
// onImage switches the "waiting" look for contrast atop a photo vs plain paper.
function StatusPill({ t, variant, text, onImage }: { t: Theme; variant: 'yourTurn' | 'waiting'; text: string; onImage?: boolean }) {
  const isAction = variant === 'yourTurn';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '4px 9px', borderRadius: 100, flexShrink: 0,
      fontFamily: FONTS.sans, fontSize: 10.5, fontWeight: 600, whiteSpace: 'nowrap',
      background: isAction ? t.terracotta : onImage ? 'rgba(0,0,0,0.5)' : t.paperAlt,
      color: isAction ? t.paper : onImage ? '#fff' : t.inkMute,
      border: isAction || onImage ? 'none' : `1px solid ${t.line}`,
      backdropFilter: onImage && !isAction ? 'blur(6px)' : undefined,
    }}>
      {!isAction && (
        <span style={{ width: 5, height: 5, borderRadius: '50%', background: t.gold, display: 'inline-block', animation: 'couplePulse 1.4s ease-in-out infinite' }} />
      )}
      {text}
    </span>
  );
}

// "Waiting" merges two backend-distinct concepts (a pick you made that your
// partner hasn't approved, and a couple your couple already confirmed liking)
// because to the user both mean the identical thing: nothing to do, in-flight.
interface WaitingItem { pick: PartnerPick; kind: 'waiting' | 'sent'; }

function buildWaitingItems(picks: PicksResponse): WaitingItem[] {
  const items: WaitingItem[] = [
    ...picks.waiting.map(p => ({ pick: p, kind: 'waiting' as const })),
    ...(picks.sent ?? []).map(p => ({ pick: p, kind: 'sent' as const })),
  ];
  return items.sort((a, b) => new Date(b.pick.createdAt).getTime() - new Date(a.pick.createdAt).getTime());
}

interface ActiveMatch {
  matchId: string;
  matchMode?: 'SINGLES' | 'COUPLES';
  currentUserId: string;
  sparkScore: number;
  sparkLabel: 'ember' | 'flame' | 'blaze';
  expiresAt: string;
  hoursRemaining: number;
  // Singles fields
  otherUser: {
    id: string;
    displayName: string;
    avatarUrl: string | null;
    photos: string[];
    bio: string | null;
    age?: number | null;
    activities?: string[];
    ethnicity?: string | null;
    nationality?: string | null;
    sparkAnswers: Array<{ prompt: string; answer: string }>;
  };
  // Couples fields
  myCouple?: CoupleSnippet;
  otherCouple?: CoupleSnippet;
  datePlan: {
    id: string;
    planTitle: string;
    planDescription: string;
    compatibilityReason: string;
    groupSize: number;
    dateProposal: DateProposal | null;
    venuePrimaryRaw: { name: string; address: string; priceRange: string } | null;
    venueAlternativeRaw: { name: string; address: string; priceRange: string } | null;
    venuePrimary: { id: string; name: string; address: string; priceRange: string } | null;
    venueAlternative: { id: string; name: string; address: string; priceRange: string } | null;
  } | null;
  chatRoomId: string | null;
}

interface MatchesScreenProps {
  t: Theme;
  dark: boolean;
}

export function MatchesScreen({ t }: MatchesScreenProps) {
  const { getIdToken, userProfile } = useAuth();
  const queryClient = useQueryClient();
  const isCouples = userProfile?.mode === 'COUPLES';
  const [openChat, setOpenChat] = useState<{ roomId: string; matchId: string; name: string; photo: string | null } | null>(null);
  const [openDatePlan, setOpenDatePlan] = useState<MatchInfo | null>(null);
  const [viewingProfile, setViewingProfile] = useState<ActiveMatch | null>(null);
  const [graduationMatch, setGraduationMatch] = useState<ActiveMatch | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  useScreenView(
    openChat ? 'chat' : openDatePlan ? 'sheet/date_plan' : viewingProfile ? 'sheet/matched_profile' : graduationMatch ? 'sheet/graduation' : null,
    'pairs',
  );

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2800);
  };

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['matches'],
    queryFn: async () => {
      const token = await getIdToken();
      const res = await fetch(`${API_BASE}/api/matches/active`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load your pairs');
      const d = await res.json();
      return d.matches as ActiveMatch[];
    },
    staleTime: 20_000,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

  const { data: picks } = useQuery({
    queryKey: ['couple-picks'],
    queryFn: async (): Promise<PicksResponse> => {
      const token = await getIdToken();
      const res = await fetch(`${API_BASE}/api/couples/picks`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch picks');
      return res.json();
    },
    enabled: isCouples,
    staleTime: 20_000,
    refetchOnWindowFocus: false,
  });

  useSSE();

  const matches = data || [];
  const waitingItems = picks ? buildWaitingItems(picks) : [];

  if (openChat) {
    const liveMatch = (data || []).find(mx => mx.matchId === openChat.matchId);
    const hasDatePlan = !!liveMatch?.datePlan;
    // Only singles chats have a single "other person" to report from the header.
    const reportTargetId = liveMatch && liveMatch.matchMode !== 'COUPLES' ? liveMatch.otherUser.id : undefined;
    return (
      <div style={{ flex: 1, minHeight: 0, position: 'relative', overflow: 'hidden' }}>
        <ChatScreen
          t={t}
          chatRoomId={openChat.roomId}
          matchId={openChat.matchId}
          hasDatePlan={hasDatePlan}
          otherUserName={openChat.name}
          otherUserPhoto={openChat.photo}
          matchInfo={{ matchId: openChat.matchId, displayName: openChat.name, avatarUrl: openChat.photo, photos: [], age: 0 }}
          reportTargetId={reportTargetId}
          onBack={() => setOpenChat(null)}
          onViewProfile={liveMatch ? () => setViewingProfile(liveMatch) : undefined}
        />
        {viewingProfile && (
          <MatchedProfileOverlay match={viewingProfile} t={t} onClose={() => setViewingProfile(null)} />
        )}
      </div>
    );
  }

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: t.paper, position: 'relative' }}>
      {toast && (
        <div style={{
          position: 'absolute', top: 14, left: '50%', transform: 'translateX(-50%)',
          background: t.ink, color: t.paper, borderRadius: 100,
          padding: '10px 18px', whiteSpace: 'nowrap', maxWidth: '88%',
          overflow: 'hidden', textOverflow: 'ellipsis',
          fontFamily: FONTS.sans, fontSize: 12.5, fontWeight: 500,
          zIndex: 250, boxShadow: t.shadow, pointerEvents: 'none',
        }}>
          {toast}
        </div>
      )}
      {openDatePlan && (
        <DateSheet match={openDatePlan} t={t} onClose={() => setOpenDatePlan(null)} />
      )}
      {viewingProfile && (
        <MatchedProfileOverlay match={viewingProfile} t={t} onClose={() => setViewingProfile(null)} />
      )}
      {graduationMatch && (
        <GraduationSheet
          matchId={graduationMatch.matchId}
          otherUser={graduationMatch.otherUser}
          t={t}
          onClose={() => setGraduationMatch(null)}
          onCoupled={() => setGraduationMatch(null)}
        />
      )}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }} className="scroll-container">

        <div style={{ padding: '14px 20px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h1 style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontWeight: 400, fontSize: 32, margin: 0, color: t.ink, letterSpacing: -0.3 }}>
            Pairs
          </h1>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            style={{ all: 'unset', cursor: isFetching ? 'default' : 'pointer', width: 34, height: 34, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: t.paperAlt, transition: 'opacity 150ms' }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ transform: 'rotate(0deg)', animation: isFetching ? 'spin 0.8s linear infinite' : 'none' }}>
              <path d="M13.5 8A5.5 5.5 0 1 1 8 2.5c1.8 0 3.4.87 4.4 2.2" stroke={t.terracotta} strokeWidth="1.6" strokeLinecap="round"/>
              <path d="M11 5h2.5V2.5" stroke={t.terracotta} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        {isLoading && <SkeletonMatches t={t} />}

        <GraduationPrompt t={t} />

        {isCouples && picks && picks.toReview.length > 0 && (
          <YourMoveSection t={t} picks={picks} getIdToken={getIdToken} queryClient={queryClient} onToast={showToast} />
        )}

        {isCouples && waitingItems.length > 0 && (
          <WaitingSection t={t} items={waitingItems} />
        )}

        {!isLoading && matches.length === 0 && (
          <div style={{ padding: '60px 32px', textAlign: 'center' }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: t.paperAlt, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              {Icon.heart({ s: 24, c: t.inkMute })}
            </div>
            <div style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 26, color: t.ink, marginBottom: 8 }}>
              No pairs yet.
            </div>
            <div style={{ fontFamily: FONTS.sans, fontSize: 14, color: t.inkMute, lineHeight: 1.6, maxWidth: 240, margin: '0 auto' }}>
              Keep exploring — someone's probably arguing with you in their head right now.
            </div>
          </div>
        )}

        {!isLoading && matches.length > 0 && (
          <div style={{ padding: '4px 20px 24px' }}>
            <div style={{ fontFamily: FONTS.sans, fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 1.5, color: t.inkMute, fontWeight: 600, marginBottom: 10 }}>
              {matches.length} active {matches.length === 1 ? 'pair' : 'pairs'}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {matches.map(m => (
                m.matchMode === 'COUPLES' && m.myCouple && m.otherCouple
                  ? <CouplesMatchCard key={m.matchId} match={m} t={t} currentUserId={m.currentUserId}
                      onOpenChat={m.chatRoomId ? () => setOpenChat({
                        roomId: m.chatRoomId!,
                        matchId: m.matchId,
                        name: `${m.otherCouple!.user1.displayName} & ${m.otherCouple!.user2.displayName}`,
                        photo: m.otherCouple!.user1.photos?.[0] || m.otherCouple!.user1.avatarUrl || null,
                      }) : undefined}
                      onPlanDate={() => setOpenDatePlan({
                        matchId: m.matchId,
                        displayName: `${m.otherCouple!.user1.displayName} & ${m.otherCouple!.user2.displayName}`,
                        avatarUrl: m.otherCouple!.user1.photos?.[0] || m.otherCouple!.user1.avatarUrl || null,
                        photos: m.otherCouple!.user1.photos || [],
                        age: 0,
                      })}
                    />
                  : <MatchCard
                      key={m.matchId}
                      match={m}
                      t={t}
                      currentUserId={m.currentUserId}
                      generatingPlan={false}
                      onViewProfile={() => setViewingProfile(m)}
                      onOpenGraduation={() => setGraduationMatch(m)}
                      onOpenChat={m.chatRoomId ? () => setOpenChat({
                        roomId: m.chatRoomId!,
                        matchId: m.matchId,
                        name: m.otherUser.displayName,
                        photo: m.otherUser.photos?.[0] || m.otherUser.avatarUrl,
                      }) : undefined}
                      onOpenDatePlan={() => setOpenDatePlan({
                        matchId: m.matchId,
                        displayName: m.otherUser.displayName,
                        avatarUrl: m.otherUser.avatarUrl,
                        photos: m.otherUser.photos,
                        age: 0,
                      })}
                    />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MatchCard({ match: m, t, currentUserId, generatingPlan, onOpenChat, onOpenDatePlan, onViewProfile, onOpenGraduation }: {
  match: ActiveMatch;
  t: Theme;
  currentUserId: string;
  generatingPlan: boolean;
  onOpenChat?: () => void;
  onOpenDatePlan?: () => void;
  onViewProfile?: () => void;
  onOpenGraduation?: () => void;
}) {
  const { userProfile, getIdToken } = useAuth();
  const queryClient = useQueryClient();

  const photo = m.otherUser.photos?.[0];
  const urgent = m.hoursRemaining < 6;
  const sparkColors = { blaze: t.terracotta, flame: t.gold, ember: t.inkMute };
  const sparkColor = sparkColors[m.sparkLabel];

  const [counterProposing, setCounterProposing] = useState(false);

  const proposal = m.datePlan?.dateProposal ?? null;
  const proposalPending = proposal?.status === 'pending';
  const iAmProposer = proposal?.proposedBy === currentUserId;
  const canRespond = proposalPending && !iAmProposer;

  const showGraduationRow = userProfile?.mode !== 'COUPLES';

  const respondMutation = useMutation({
    mutationFn: async (action: 'confirm' | 'decline') => {
      const token = await getIdToken();
      const res = await fetch(`${API_BASE}/api/matches/${m.matchId}/date-proposal/${action}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed');
    },
    onMutate: async (action) => {
      await queryClient.cancelQueries({ queryKey: ['matches'] });
      const prev = queryClient.getQueryData<any[]>(['matches']);
      queryClient.setQueryData<any[]>(['matches'], old =>
        old?.map(match => match.matchId !== m.matchId ? match : {
          ...match,
          datePlan: match.datePlan ? {
            ...match.datePlan,
            dateProposal: match.datePlan.dateProposal
              ? { ...match.datePlan.dateProposal, status: action === 'confirm' ? 'confirmed' : 'declined' }
              : match.datePlan.dateProposal,
          } : null,
        })
      );
      return { prev };
    },
    onError: (_err, _action, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['matches'], ctx.prev);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['matches'] }),
  });

  return (
    <div style={{ borderRadius: 20, overflow: 'hidden', background: t.card, border: `1px solid ${t.line}`, boxShadow: t.shadow }}>
      <div style={{ display: 'flex' }}>
        {/* Photo — tappable to view profile */}
        <button onClick={onViewProfile} aria-label={`View ${m.otherUser.displayName}'s profile`} style={{ all: 'unset', width: 90, height: 110, flexShrink: 0, position: 'relative', cursor: onViewProfile ? 'pointer' : 'default', display: 'block' }}>
          {photo
            ? <Img placeholder={t.paperAlt} src={photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <Portrait seed={m.otherUser.id} style={{ width: '100%', height: '100%' }} />}
        </button>

        {/* Info */}
        <div style={{ flex: 1, padding: '14px 14px 14px 14px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minWidth: 0 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <span style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 22, color: t.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {m.otherUser.displayName}
              </span>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '3px 8px', borderRadius: 100, flexShrink: 0,
                fontFamily: FONTS.mono, fontSize: 10, fontWeight: 600,
                color: sparkColor, border: `1px solid ${sparkColor}`,
              }}>
                {Icon.sparkle({ s: 9, c: sparkColor })} {m.sparkScore}
              </span>
            </div>
            {canRespond && (
              <div style={{ marginTop: 5 }}>
                <StatusPill t={t} variant="yourTurn" text="Your turn to respond" />
              </div>
            )}
            {m.otherUser.bio && (
              <div style={{ fontFamily: FONTS.sans, fontSize: 12, color: t.inkSoft, marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {m.otherUser.bio}
              </div>
            )}
          </div>

          {m.hoursRemaining <= 720 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginTop: 8 }}>
              <span style={{ fontFamily: FONTS.mono, fontSize: 10, color: urgent ? t.terracotta : t.inkMute, fontWeight: urgent ? 600 : 400 }}>
                {m.hoursRemaining}h left
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Pending proposal banner */}
      {proposalPending && (
        <div style={{
          borderTop: `1px solid ${t.line}`,
          padding: '10px 14px',
          background: t.paperAlt,
        }}>
          <div style={{ fontFamily: FONTS.sans, fontSize: 11, color: t.inkMute, marginBottom: 6 }}>
            {iAmProposer
              ? `Waiting for ${m.otherUser.displayName} to respond...`
              : `${m.otherUser.displayName} proposed:`}
          </div>
          <div style={{ fontFamily: FONTS.sans, fontSize: 13, color: t.ink, fontWeight: 600, marginBottom: canRespond ? 10 : 0 }}>
            {proposal!.venueName} · {new Date(`${proposal!.date}T00:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} at {proposal!.time}
          </div>
          {canRespond && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => respondMutation.mutate('confirm')}
                  disabled={respondMutation.isPending}
                  style={{
                    all: 'unset', cursor: 'pointer', flex: 1, textAlign: 'center',
                    padding: '7px 0', borderRadius: 10,
                    background: t.terracotta, color: t.paper,
                    fontFamily: FONTS.sans, fontSize: 12, fontWeight: 600,
                    opacity: respondMutation.isPending ? 0.5 : 1,
                  }}
                >Works for me</button>
                <button
                  onClick={() => setCounterProposing(true)}
                  disabled={respondMutation.isPending}
                  style={{
                    all: 'unset', cursor: 'pointer', flex: 1, textAlign: 'center',
                    padding: '7px 0', borderRadius: 10,
                    background: 'transparent', color: t.ink,
                    fontFamily: FONTS.sans, fontSize: 12, fontWeight: 500,
                    border: `1px solid ${t.line}`,
                    opacity: respondMutation.isPending ? 0.5 : 1,
                  }}
                >Suggest another time</button>
              </div>
              <button
                onClick={() => respondMutation.mutate('decline')}
                disabled={respondMutation.isPending}
                style={{
                  all: 'unset', cursor: 'pointer', textAlign: 'center',
                  padding: '4px 0',
                  fontFamily: FONTS.sans, fontSize: 11, color: t.inkMute,
                  opacity: respondMutation.isPending ? 0.5 : 1,
                }}
              >Not for me</button>
            </div>
          )}
          {counterProposing && m.datePlan && (
            <DateScheduler
              matchId={m.matchId}
              datePlanId={m.datePlan.id}
              otherUserName={m.otherUser.displayName}
              venues={[
                ...(m.datePlan.venuePrimary ? [{ id: m.datePlan.venuePrimary.id, name: m.datePlan.venuePrimary.name, address: m.datePlan.venuePrimary.address, priceRange: m.datePlan.venuePrimary.priceRange }] : []),
                ...(m.datePlan.venueAlternative ? [{ id: m.datePlan.venueAlternative.id, name: m.datePlan.venueAlternative.name, address: m.datePlan.venueAlternative.address, priceRange: m.datePlan.venueAlternative.priceRange }] : []),
              ]}
              groupSize={m.datePlan.groupSize ?? 2}
              t={t}
              initialVenueName={proposal?.venueName}
              onClose={() => setCounterProposing(false)}
              onProposed={() => setCounterProposing(false)}
            />
          )}
        </div>
      )}

      {/* Confirmed proposal banner */}
      {proposal?.status === 'confirmed' && (
        <div style={{
          borderTop: `1px solid ${t.line}`,
          padding: '10px 14px',
          background: t.paperAlt,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          {Icon.sparkle({ s: 13, c: t.sage })}
          <span style={{ fontFamily: FONTS.sans, fontSize: 12, color: t.sage, fontWeight: 600 }}>
            Date confirmed · {proposal.venueName} · {new Date(`${proposal.date}T00:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} at {proposal.time}
          </span>
        </div>
      )}

      {/* Bottom action bar */}
      <div style={{ borderTop: `1px solid ${t.line}`, background: t.paperAlt }}>
        {/* Date plan row — shows plan title if exists, otherwise "Ask Concierge" */}
        {m.datePlan ? (
          <button onClick={onOpenDatePlan} className="pm-press-soft" style={{
            all: 'unset', cursor: 'pointer', width: '100%', boxSizing: 'border-box',
            padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8,
            borderBottom: m.chatRoomId ? `1px solid ${t.line}` : 'none',
          }}>
            {Icon.sparkle({ s: 13, c: t.terracotta })}
            <span style={{ fontFamily: FONTS.sans, fontSize: 12, color: t.ink, fontWeight: 500 }}>
              {m.datePlan.planTitle}
            </span>
            <span style={{ marginLeft: 'auto', fontFamily: FONTS.sans, fontSize: 12, color: t.terracotta, fontWeight: 600 }}>View →</span>
          </button>
        ) : (
          <button onClick={generatingPlan ? undefined : onOpenDatePlan} disabled={generatingPlan} style={{
            all: 'unset', cursor: generatingPlan ? 'default' : 'pointer',
            width: '100%', boxSizing: 'border-box',
            padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8,
            borderBottom: m.chatRoomId ? `1px solid ${t.line}` : 'none',
          }}>
            {generatingPlan
              ? <div style={{ width: 13, height: 13, borderRadius: '50%', border: `2px solid ${t.terracotta}`, borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
              : Icon.sparkle({ s: 14, c: t.terracotta })}
            <span style={{ fontFamily: FONTS.sans, fontSize: 12, color: generatingPlan ? t.inkMute : t.terracotta, fontWeight: 600 }}>
              {generatingPlan ? 'Concierge is thinking...' : 'Ask Concierge for date ideas'}
            </span>
            {!generatingPlan && <span style={{ marginLeft: 'auto', fontFamily: FONTS.mono, fontSize: 10, color: t.inkMute }}>✦</span>}
          </button>
        )}
        {/* Chat row */}
        {m.chatRoomId && (
          <button onClick={onOpenChat} className="pm-press-soft" style={{
            all: 'unset', cursor: 'pointer', width: '100%', boxSizing: 'border-box',
            padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8,
            borderBottom: showGraduationRow ? `1px solid ${t.line}` : 'none',
          }}>
            {Icon.chat({ s: 14, c: t.terracotta })}
            <span style={{ fontFamily: FONTS.sans, fontSize: 12, color: t.terracotta, fontWeight: 600 }}>
              Chat with {m.otherUser.displayName}
            </span>
            <span style={{ marginLeft: 'auto', fontFamily: FONTS.mono, fontSize: 10, color: t.inkMute }}>→</span>
          </button>
        )}
        {/* Couples graduation row */}
        {showGraduationRow && (
          <button
            onClick={onOpenGraduation}
            className="pm-press"
            style={{
              all: 'unset',
              cursor: 'pointer',
              width: '100%', boxSizing: 'border-box',
              padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8,
            }}
          >
            <span style={{ fontSize: 12, lineHeight: 1 }}>♥♥</span>
            <span style={{ fontFamily: FONTS.sans, fontSize: 12, color: t.gold, fontWeight: 600 }}>
              Try double dates
            </span>
            <span style={{ marginLeft: 'auto', fontFamily: FONTS.mono, fontSize: 10, color: t.gold }}>→</span>
          </button>
        )}
      </div>
    </div>
  );
}

// The only always-loud couples section: picks that need YOUR approval before
// they're sent to the other couple. Everything else in couples mode is a
// "waiting" state and lives in the quieter WaitingSection below.
function YourMoveSection({ t, picks, getIdToken, queryClient, onToast }: {
  t: Theme;
  picks: PicksResponse;
  getIdToken: () => Promise<string | null>;
  queryClient: QueryClient;
  onToast: (msg: string) => void;
}) {
  const [detail, setDetail] = useState<PartnerPick | null>(null);

  const respond = useMutation({
    mutationFn: async ({ likeId, approve }: { likeId: string; approve: boolean }) => {
      const token = await getIdToken();
      const res = await fetch(`${API_BASE}/api/couples/picks/${likeId}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ approve }),
      });
      if (!res.ok) throw new Error('Failed to respond');
      return res.json() as Promise<{ isMatch: boolean }>;
    },
    onMutate: async ({ likeId }) => {
      await queryClient.cancelQueries({ queryKey: ['couple-picks'] });
      const prev = queryClient.getQueryData<PicksResponse>(['couple-picks']);
      queryClient.setQueryData<PicksResponse>(['couple-picks'], old => old ? {
        ...old,
        toReview: old.toReview.filter(p => p.likeId !== likeId),
      } : old);
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['couple-picks'], ctx.prev);
    },
    onSuccess: (result, variables) => {
      if (result.isMatch) {
        queryClient.invalidateQueries({ queryKey: ['matches'] });
        onToast("♥♥ You've been paired!");
      } else if (variables.approve) {
        // Approved but not mutual yet — tell them what happens next, otherwise
        // the pick just silently disappears and feels broken.
        onToast('Sent — now it\'s their couple\'s move ♥♥');
      }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['couple-picks'] }),
  });

  return (
    <div style={{ padding: '4px 0 16px' }}>
      <div style={{ padding: '0 20px', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
        <span style={{ color: t.terracotta, fontSize: 12, lineHeight: 1 }}>♥♥</span>
        <span style={{ fontFamily: FONTS.sans, fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 1.5, color: t.terracotta, fontWeight: 700 }}>
          Your move
        </span>
        <span style={{ fontFamily: FONTS.sans, fontSize: 10.5, color: t.inkMute }}>· {picks.toReview.length}</span>
      </div>
      <div style={{ display: 'flex', gap: 10, overflowX: 'auto', padding: '0 20px 4px', WebkitOverflowScrolling: 'touch' }} className="scroll-container">
        {picks.toReview.map(p => (
          <PickCard key={p.likeId} t={t} pick={p} mode="review"
            onOpenDetail={() => setDetail(p)}
            onLike={() => { hapticLight(); respond.mutate({ likeId: p.likeId, approve: true }); }}
            onPass={() => { hapticLight(); respond.mutate({ likeId: p.likeId, approve: false }); }}
          />
        ))}
      </div>

      {detail && (
        <PickDetailOverlay
          t={t}
          pick={detail}
          mode="review"
          onClose={() => setDetail(null)}
          onLike={() => { hapticLight(); respond.mutate({ likeId: detail.likeId, approve: true }); setDetail(null); }}
          onPass={() => { hapticLight(); respond.mutate({ likeId: detail.likeId, approve: false }); setDetail(null); }}
        />
      )}
    </div>
  );
}

// Quiet, merged "nothing to do yet" bucket — picks awaiting your partner's yes
// AND couples your couple already confirmed liking (both are the same feeling
// to the user: in-flight, not theirs to touch). Read-only besides the tap-in.
function WaitingSection({ t, items }: { t: Theme; items: WaitingItem[] }) {
  const [detail, setDetail] = useState<WaitingItem | null>(null);
  return (
    <div style={{ padding: '4px 0 16px' }}>
      <div style={{ padding: '0 20px', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
        <span style={{ fontFamily: FONTS.sans, fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 1.5, color: t.inkMute, fontWeight: 600 }}>
          Waiting
        </span>
        <span style={{ fontFamily: FONTS.sans, fontSize: 10.5, color: t.inkMute }}>· {items.length}</span>
      </div>
      <div style={{ display: 'flex', gap: 10, overflowX: 'auto', padding: '0 20px 4px', WebkitOverflowScrolling: 'touch' }} className="scroll-container">
        {items.map(item => (
          <PickCard key={item.pick.likeId} t={t} pick={item.pick} mode={item.kind} onOpenDetail={() => setDetail(item)} />
        ))}
      </div>
      {detail && (
        <PickDetailOverlay t={t} pick={detail.pick} mode={detail.kind} onClose={() => setDetail(null)} />
      )}
    </div>
  );
}

// Full-screen detail view for a partner's pick — the reviewing partner sees
// the same profile their partner saw in the discover feed before deciding.
function PickDetailOverlay({ t, pick, mode, onClose, onLike, onPass }: {
  t: Theme;
  pick: PartnerPick;
  mode: 'review' | 'waiting' | 'sent';
  onClose: () => void;
  onLike?: () => void;
  onPass?: () => void;
}) {
  const c = pick.targetCouple;
  const candidate: CoupleFeedCandidate = {
    coupleId: c.coupleId,
    user1: { name: c.user1.displayName, age: c.user1.age ?? 0, avatarUrl: c.user1.avatarUrl, photos: c.user1.photos || [] },
    user2: { name: c.user2.displayName, age: c.user2.age ?? 0, avatarUrl: c.user2.avatarUrl, photos: c.user2.photos || [] },
    coupleBio: c.coupleBio,
    sparkScore: c.sparkScore ?? 0,
    sparkLabel: c.sparkLabel ?? 'ember',
    compatibilityHint: c.compatibilityHint ?? '',
    combinedActivities: c.combinedActivities ?? [],
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.3)' }}>
      <div style={{
        width: '100%', maxWidth: 430, height: '100%', margin: '0 auto',
        background: t.paper, display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: `1px solid ${t.line}`, flexShrink: 0 }}>
          <button onClick={onClose} className="pm-press" style={{
            all: 'unset', cursor: 'pointer', width: 36, height: 36,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: '50%', background: t.paperAlt, flexShrink: 0,
          }}>
            {Icon.back({ s: 18, c: t.ink })}
          </button>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 18, color: t.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {c.user1.displayName} & {c.user2.displayName}
            </div>
            <div style={{ marginTop: 4 }}>
              <StatusPill
                t={t}
                variant={mode === 'review' ? 'yourTurn' : 'waiting'}
                text={mode === 'review' ? 'Your turn' : mode === 'sent' ? `Sent · ${timeAgo(pick.createdAt)}` : `Needs ${pick.awaitingName || 'partner'}`}
              />
            </div>
          </div>
        </div>

        {/* Scrollable profile — same view as the discover feed */}
        <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
          <CoupleProfileView couple={candidate} t={t} style={{ position: 'absolute', inset: 0 }} />
        </div>

        {/* Actions */}
        <div style={{
          flexShrink: 0, padding: '12px 20px', borderTop: `1px solid ${t.line}`,
          paddingBottom: 'max(16px, env(safe-area-inset-bottom))', background: t.paper,
        }}>
          {mode === 'review' ? (
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={onPass} className="pm-press-soft" style={{
                all: 'unset', cursor: 'pointer', flex: 1, textAlign: 'center',
                padding: '13px 0', borderRadius: 14, border: `1px solid ${t.line}`,
                fontFamily: FONTS.sans, fontSize: 14, fontWeight: 500, color: t.inkSoft,
              }}>Pass</button>
              <button onClick={onLike} className="pm-press-soft" style={{
                all: 'unset', cursor: 'pointer', flex: 1.4, textAlign: 'center',
                padding: '13px 0', borderRadius: 14, background: t.gold, color: t.paper,
                fontFamily: FONTS.sans, fontSize: 14, fontWeight: 600,
              }}>♥ Like too</button>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '6px 0' }}>
              <span style={{ fontFamily: FONTS.sans, fontSize: 13, color: t.inkMute }}>
                {mode === 'sent' ? "You both said yes — waiting for their couple to decide" : `Waiting on ${pick.awaitingName || 'your partner'} to say yes too`}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PickCard({ t, pick, mode, onOpenDetail, onLike, onPass }: {
  t: Theme;
  pick: PartnerPick;
  mode: 'review' | 'waiting' | 'sent';
  onOpenDetail?: () => void;
  onLike?: () => void;
  onPass?: () => void;
}) {
  const c = pick.targetCouple;
  const photo1 = c.user1.photos?.[0] || c.user1.avatarUrl;
  const photo2 = c.user2.photos?.[0] || c.user2.avatarUrl;
  const isYourTurn = mode === 'review';
  // Corner pill carries the state at a glance; the text line beneath the names
  // adds context (who/what) without repeating the pill's "what to do" signal.
  const pillText = isYourTurn ? 'Your turn' : mode === 'sent' ? `Sent · ${timeAgo(pick.createdAt)}` : `Needs ${pick.awaitingName || 'partner'}`;
  const contextLabel = isYourTurn ? `Picked by ${pick.pickedBy}` : mode === 'sent' ? 'You both liked them' : 'You picked them';

  return (
    <div style={{
      flexShrink: 0, width: 200, borderRadius: 16, overflow: 'hidden',
      background: t.card, border: `1px solid ${isYourTurn ? t.terracotta : t.line}`,
    }}>
      {/* Photos + names — tappable to view the full couple profile before deciding */}
      <button onClick={onOpenDetail} style={{ all: 'unset', cursor: onOpenDetail ? 'pointer' : 'default', display: 'block', width: '100%' }}>
        <div style={{ position: 'relative', display: 'flex', gap: 3, height: 100, opacity: isYourTurn ? 1 : 0.88 }}>
          <div style={{ flex: 1 }}>
            {photo1
              ? <Img placeholder={t.paperAlt} src={photo1} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <Portrait seed={c.user1.displayName} style={{ width: '100%', height: '100%' }} />}
          </div>
          <div style={{ flex: 1 }}>
            {photo2
              ? <Img placeholder={t.paperAlt} src={photo2} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <Portrait seed={c.user2.displayName} style={{ width: '100%', height: '100%' }} />}
          </div>
          <div style={{ position: 'absolute', top: 8, left: 8 }}>
            <StatusPill t={t} variant={isYourTurn ? 'yourTurn' : 'waiting'} text={pillText} onImage />
          </div>
        </div>

        <div style={{ padding: '10px 12px 0' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 6, marginBottom: 2 }}>
            <div style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 15, color: t.ink, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {c.user1.displayName} & {c.user2.displayName}
            </div>
            {onOpenDetail && <span style={{ fontFamily: FONTS.mono, fontSize: 10, color: t.terracotta, flexShrink: 0 }}>→</span>}
          </div>
          <div style={{ fontFamily: FONTS.sans, fontSize: 10.5, color: t.inkMute }}>
            {contextLabel}
          </div>
        </div>
      </button>

      {isYourTurn ? (
        <div style={{ padding: '10px 12px 12px' }}>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={onPass} className="pm-press-soft" style={{
              all: 'unset', cursor: 'pointer', flex: 1, textAlign: 'center',
              padding: '8px 0', borderRadius: 10, border: `1px solid ${t.line}`,
              fontFamily: FONTS.sans, fontSize: 12, fontWeight: 500, color: t.inkSoft,
            }}>Pass</button>
            <button onClick={onLike} className="pm-press-soft" style={{
              all: 'unset', cursor: 'pointer', flex: 1.4, textAlign: 'center',
              padding: '8px 0', borderRadius: 10, background: t.terracotta, color: t.paper,
              fontFamily: FONTS.sans, fontSize: 12, fontWeight: 600,
            }}>♥ Like too</button>
          </div>
        </div>
      ) : (
        <div style={{ height: 12 }} />
      )}
    </div>
  );
}

function CouplesMatchCard({ match: m, t, currentUserId, onOpenChat, onPlanDate }: {
  match: ActiveMatch; t: Theme; currentUserId: string;
  onOpenChat?: () => void;
  onPlanDate?: () => void;
}) {
  const { getIdToken } = useAuth();
  const queryClient = useQueryClient();
  const my = m.myCouple!;
  const other = m.otherCouple!;
  const otherCoupleName = `${other.user1.displayName} & ${other.user2.displayName}`;
  const sparkColors = { blaze: t.terracotta, flame: t.gold, ember: t.inkMute };
  const sparkColor = sparkColors[m.sparkLabel];
  const urgent = m.hoursRemaining < 6;

  const myPhoto1 = my.user1.photos?.[0] || my.user1.avatarUrl;
  const myPhoto2 = my.user2.photos?.[0] || my.user2.avatarUrl;
  const otherPhoto1 = other.user1.photos?.[0] || other.user1.avatarUrl;
  const otherPhoto2 = other.user2.photos?.[0] || other.user2.avatarUrl;

  const [counterProposing, setCounterProposing] = useState(false);
  const proposal = m.datePlan?.dateProposal ?? null;
  const proposalPending = proposal?.status === 'pending';
  const iAmProposer = proposal?.proposedBy === currentUserId;
  const canRespond = proposalPending && !iAmProposer;

  const respondMutation = useMutation({
    mutationFn: async (action: 'confirm' | 'decline') => {
      const token = await getIdToken();
      const res = await fetch(`${API_BASE}/api/matches/${m.matchId}/date-proposal/${action}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed');
    },
    onMutate: async (action) => {
      await queryClient.cancelQueries({ queryKey: ['matches'] });
      const prev = queryClient.getQueryData<any[]>(['matches']);
      queryClient.setQueryData<any[]>(['matches'], old =>
        old?.map(match => match.matchId !== m.matchId ? match : {
          ...match,
          datePlan: match.datePlan ? {
            ...match.datePlan,
            dateProposal: match.datePlan.dateProposal
              ? { ...match.datePlan.dateProposal, status: action === 'confirm' ? 'confirmed' : 'declined' }
              : match.datePlan.dateProposal,
          } : null,
        })
      );
      return { prev };
    },
    onError: (_err, _action, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['matches'], ctx.prev);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['matches'] }),
  });

  return (
    <div style={{ borderRadius: 20, overflow: 'hidden', background: t.card, border: `1px solid ${t.gold}55`, boxShadow: `0 2px 16px ${t.gold}18` }}>
      {/* Header */}
      <div style={{ padding: '12px 14px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${t.line}` }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ fontFamily: FONTS.sans, fontSize: 10, textTransform: 'uppercase', letterSpacing: 1.2, color: t.gold, fontWeight: 600 }}>Double Pair</span>
          <span style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 18, color: t.ink }}>You've been paired!</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {canRespond && <StatusPill t={t} variant="yourTurn" text="Your turn" />}
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '3px 8px', borderRadius: 100,
            fontFamily: FONTS.mono, fontSize: 10, fontWeight: 600,
            color: sparkColor, border: `1px solid ${sparkColor}`,
          }}>
            {Icon.sparkle({ s: 9, c: sparkColor })} {m.sparkScore}
          </span>
        </div>
      </div>

      {/* Dual couple photos */}
      <div style={{ display: 'flex', padding: '12px 12px 8px', gap: 10 }}>
        {/* My couple */}
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: FONTS.sans, fontSize: 10, color: t.inkMute, marginBottom: 6, textAlign: 'center', textTransform: 'uppercase', letterSpacing: 0.8 }}>You two</div>
          <div style={{ display: 'flex', gap: 4, borderRadius: 14, overflow: 'hidden', height: 88 }}>
            <div style={{ flex: 1, position: 'relative' }}>
              {myPhoto1
                ? <Img placeholder={t.paperAlt} src={myPhoto1} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <Portrait seed={my.user1.displayName} style={{ width: '100%', height: '100%' }} />
              }
            </div>
            <div style={{ width: 2, background: t.paper, flexShrink: 0 }} />
            <div style={{ flex: 1, position: 'relative' }}>
              {myPhoto2
                ? <Img placeholder={t.paperAlt} src={myPhoto2} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <Portrait seed={my.user2.displayName} style={{ width: '100%', height: '100%' }} />
              }
            </div>
          </div>
          <div style={{ marginTop: 6, fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 13, color: t.ink, textAlign: 'center', lineHeight: 1.2 }}>
            {my.user1.displayName} &<br />{my.user2.displayName}
          </div>
        </div>

        {/* VS divider */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, flexShrink: 0 }}>
          <div style={{ width: 1, flex: 1, background: t.line }} />
          <span style={{ fontFamily: FONTS.mono, fontSize: 10, color: t.gold, fontWeight: 700 }}>♥♥</span>
          <div style={{ width: 1, flex: 1, background: t.line }} />
        </div>

        {/* Other couple */}
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: FONTS.sans, fontSize: 10, color: t.inkMute, marginBottom: 6, textAlign: 'center', textTransform: 'uppercase', letterSpacing: 0.8 }}>Them</div>
          <div style={{ display: 'flex', gap: 4, borderRadius: 14, overflow: 'hidden', height: 88 }}>
            <div style={{ flex: 1, position: 'relative' }}>
              {otherPhoto1
                ? <Img placeholder={t.paperAlt} src={otherPhoto1} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <Portrait seed={other.user1.displayName} style={{ width: '100%', height: '100%' }} />
              }
            </div>
            <div style={{ width: 2, background: t.paper, flexShrink: 0 }} />
            <div style={{ flex: 1, position: 'relative' }}>
              {otherPhoto2
                ? <Img placeholder={t.paperAlt} src={otherPhoto2} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <Portrait seed={other.user2.displayName} style={{ width: '100%', height: '100%' }} />
              }
            </div>
          </div>
          <div style={{ marginTop: 6, fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 13, color: t.ink, textAlign: 'center', lineHeight: 1.2 }}>
            {other.user1.displayName} &<br />{other.user2.displayName}
          </div>
        </div>
      </div>

      {/* Timer */}
      {m.hoursRemaining <= 720 && (
        <div style={{ padding: '0 14px 10px', textAlign: 'right' }}>
          <span style={{ fontFamily: FONTS.mono, fontSize: 10, color: urgent ? t.terracotta : t.inkMute, fontWeight: urgent ? 600 : 400 }}>
            {m.hoursRemaining}h left
          </span>
        </div>
      )}

      {/* Pending proposal banner */}
      {proposalPending && (
        <div style={{ borderTop: `1px solid ${t.line}`, padding: '10px 14px', background: t.paperAlt }}>
          <div style={{ fontFamily: FONTS.sans, fontSize: 11, color: t.inkMute, marginBottom: 6 }}>
            {iAmProposer ? `Waiting for ${otherCoupleName} to respond...` : `${otherCoupleName} proposed:`}
          </div>
          <div style={{ fontFamily: FONTS.sans, fontSize: 13, color: t.ink, fontWeight: 600, marginBottom: canRespond ? 10 : 0 }}>
            {proposal!.venueName} · {new Date(`${proposal!.date}T00:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} at {proposal!.time}
          </div>
          {canRespond && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => respondMutation.mutate('confirm')}
                  disabled={respondMutation.isPending}
                  style={{
                    all: 'unset', cursor: 'pointer', flex: 1, textAlign: 'center',
                    padding: '7px 0', borderRadius: 10,
                    background: t.gold, color: '#fff',
                    fontFamily: FONTS.sans, fontSize: 12, fontWeight: 600,
                    opacity: respondMutation.isPending ? 0.5 : 1,
                  }}
                >Works for us</button>
                <button
                  onClick={() => setCounterProposing(true)}
                  disabled={respondMutation.isPending}
                  style={{
                    all: 'unset', cursor: 'pointer', flex: 1, textAlign: 'center',
                    padding: '7px 0', borderRadius: 10,
                    background: 'transparent', color: t.ink,
                    fontFamily: FONTS.sans, fontSize: 12, fontWeight: 500,
                    border: `1px solid ${t.line}`,
                    opacity: respondMutation.isPending ? 0.5 : 1,
                  }}
                >Suggest another time</button>
              </div>
              <button
                onClick={() => respondMutation.mutate('decline')}
                disabled={respondMutation.isPending}
                style={{
                  all: 'unset', cursor: 'pointer', textAlign: 'center',
                  padding: '4px 0',
                  fontFamily: FONTS.sans, fontSize: 11, color: t.inkMute,
                  opacity: respondMutation.isPending ? 0.5 : 1,
                }}
              >Not for us</button>
            </div>
          )}
          {counterProposing && m.datePlan && (
            <DateScheduler
              matchId={m.matchId}
              datePlanId={m.datePlan.id}
              otherUserName={otherCoupleName}
              venues={[
                ...(m.datePlan.venuePrimary ? [{ id: m.datePlan.venuePrimary.id, name: m.datePlan.venuePrimary.name, address: m.datePlan.venuePrimary.address, priceRange: m.datePlan.venuePrimary.priceRange }] : []),
                ...(m.datePlan.venueAlternative ? [{ id: m.datePlan.venueAlternative.id, name: m.datePlan.venueAlternative.name, address: m.datePlan.venueAlternative.address, priceRange: m.datePlan.venueAlternative.priceRange }] : []),
              ]}
              groupSize={m.datePlan.groupSize ?? 4}
              t={t}
              initialVenueName={proposal?.venueName}
              onClose={() => setCounterProposing(false)}
              onProposed={() => setCounterProposing(false)}
            />
          )}
        </div>
      )}

      {/* Confirmed proposal banner */}
      {proposal?.status === 'confirmed' && (
        <div style={{ borderTop: `1px solid ${t.line}`, padding: '10px 14px', background: t.paperAlt, display: 'flex', alignItems: 'center', gap: 8 }}>
          {Icon.sparkle({ s: 13, c: t.sage })}
          <span style={{ fontFamily: FONTS.sans, fontSize: 12, color: t.sage, fontWeight: 600 }}>
            Date confirmed · {proposal.venueName} · {new Date(`${proposal.date}T00:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} at {proposal.time}
          </span>
        </div>
      )}

      {/* Bottom bar */}
      <div style={{ borderTop: `1px solid ${t.line}`, background: t.paperAlt }}>
        {m.chatRoomId && onOpenChat ? (
          <button onClick={onOpenChat} className="pm-press-soft" style={{
            all: 'unset', cursor: 'pointer', width: '100%', boxSizing: 'border-box',
            padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8,
            borderBottom: `1px solid ${t.line}`,
          }}>
            {Icon.chat({ s: 14, c: t.gold })}
            <span style={{ fontFamily: FONTS.sans, fontSize: 12, color: t.gold, fontWeight: 600 }}>
              Chat with all four
            </span>
            <span style={{ marginLeft: 'auto', fontFamily: FONTS.mono, fontSize: 10, color: t.gold }}>→</span>
          </button>
        ) : null}
        {m.datePlan ? (
          <button onClick={onPlanDate} className="pm-press-soft" style={{
            all: 'unset', cursor: 'pointer', width: '100%', boxSizing: 'border-box',
            padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8,
          }}>
            {Icon.sparkle({ s: 13, c: t.terracotta })}
            <span style={{ fontFamily: FONTS.sans, fontSize: 12, color: t.terracotta, fontWeight: 600 }}>
              {m.datePlan.planTitle}
            </span>
            <span style={{ marginLeft: 'auto', fontFamily: FONTS.mono, fontSize: 10, color: t.terracotta }}>→</span>
          </button>
        ) : (
          <button onClick={onPlanDate} className="pm-press-soft" style={{
            all: 'unset', cursor: 'pointer', width: '100%', boxSizing: 'border-box',
            padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8,
          }}>
            {Icon.sparkle({ s: 13, c: t.inkSoft })}
            <span style={{ fontFamily: FONTS.sans, fontSize: 12, color: t.ink, fontWeight: 500 }}>
              Plan your double date
            </span>
            <span style={{ marginLeft: 'auto', fontFamily: FONTS.mono, fontSize: 10, color: t.inkMute }}>+</span>
          </button>
        )}
      </div>
    </div>
  );
}

function MatchedProfileOverlay({ match: m, t, onClose }: { match: ActiveMatch; t: Theme; onClose: () => void }) {
  const photos = m.otherUser.photos?.length ? m.otherUser.photos : m.otherUser.avatarUrl ? [m.otherUser.avatarUrl] : [];
  const [photoIndex, setPhotoIndex] = useState(0);
  const [showReport, setShowReport] = useState(false);
  const sparkColors = { blaze: t.terracotta, flame: t.gold, ember: t.inkMute };
  const sparkColor = sparkColors[m.sparkLabel];

  return (
    <div style={{ position: 'absolute', inset: 0, zIndex: 200, background: t.paper, display: 'flex', flexDirection: 'column' }}>
      {showReport && (
        <ReportBlockSheet
          t={t}
          targetUserId={m.otherUser.id}
          targetName={m.otherUser.displayName}
          onClose={() => setShowReport(false)}
          onDone={onClose}
        />
      )}
      <div style={{ flex: 1, overflowY: 'auto' }} className="scroll-container">

        {/* Hero photo */}
        <div style={{ position: 'relative', height: 480, background: t.paperAlt, flexShrink: 0 }}>
          {photos[photoIndex] ? (
            <Img placeholder={t.paperAlt} src={photos[photoIndex]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <Portrait seed={m.otherUser.id} style={{ width: '100%', height: '100%' }} />
          )}

          {/* Gradient */}
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.55) 0%, transparent 50%)' }} />

          {/* Back */}
          <button onClick={onClose} style={{
            all: 'unset', cursor: 'pointer', position: 'absolute', top: 12, left: 16,
            width: 40, height: 40, borderRadius: '50%',
            background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(10px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {Icon.back({ s: 20, c: '#fff' })}
          </button>

          {/* Report / block */}
          <button onClick={() => setShowReport(true)} aria-label="Report or block" style={{
            all: 'unset', cursor: 'pointer', position: 'absolute', top: 12, right: 16,
            width: 40, height: 40, borderRadius: '50%',
            background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(10px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {Icon.more({ s: 20, c: '#fff' })}
          </button>

          {/* Photo dots */}
          {photos.length > 1 && (
            <div style={{ position: 'absolute', top: 16, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 4 }}>
              {photos.map((_, i) => (
                <button key={i} onClick={() => setPhotoIndex(i)} style={{
                  all: 'unset', cursor: 'pointer',
                  width: i === photoIndex ? 20 : 6, height: 6, borderRadius: 3,
                  background: i === photoIndex ? '#fff' : 'rgba(255,255,255,0.45)',
                  transition: 'width 200ms',
                }} />
              ))}
            </div>
          )}

          {/* Tap left/right to browse photos */}
          {photos.length > 1 && (
            <>
              <button onClick={() => setPhotoIndex(i => Math.max(0, i - 1))} style={{ all: 'unset', position: 'absolute', left: 0, top: 0, bottom: 0, width: '35%', cursor: 'pointer' }} />
              <button onClick={() => setPhotoIndex(i => Math.min(photos.length - 1, i + 1))} style={{ all: 'unset', position: 'absolute', right: 0, top: 0, bottom: 0, width: '35%', cursor: 'pointer' }} />
            </>
          )}

          {/* Name + spark */}
          <div style={{ position: 'absolute', bottom: 16, left: 16, right: 16, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
            <span style={{ display: 'flex', alignItems: 'baseline', gap: 9 }}>
              <span style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 32, color: '#fff', fontWeight: 400, letterSpacing: -0.5 }}>
                {m.otherUser.displayName}
              </span>
              {!!m.otherUser.age && (
                <span style={{ fontFamily: FONTS.sans, fontSize: 22, fontWeight: 300, color: '#fff' }}>{m.otherUser.age}</span>
              )}
            </span>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '4px 10px', borderRadius: 100, flexShrink: 0,
              fontFamily: FONTS.mono, fontSize: 11, fontWeight: 600,
              color: sparkColor, background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(8px)',
              border: `1px solid ${sparkColor}55`,
            }}>
              {Icon.sparkle({ s: 10, c: sparkColor })} {m.sparkScore}
            </span>
          </div>
        </div>

        {/* Bio */}
        {m.otherUser.bio && (
          <div style={{ padding: '20px 20px 4px' }}>
            <p style={{ fontFamily: FONTS.sans, fontSize: 15, color: t.ink, lineHeight: 1.6, margin: 0 }}>
              {m.otherUser.bio}
            </p>
          </div>
        )}

        {/* Identity chips (optional fields) */}
        {(() => {
          const chips = [ethnicityLabel(m.otherUser.ethnicity), m.otherUser.nationality].filter((v): v is string => !!v);
          return chips.length > 0 ? (
            <div data-clarity-mask="true" style={{ padding: '14px 20px 0', display: 'flex', flexWrap: 'wrap', gap: 7 }}>
              {chips.map((chip, i) => (
                <span key={i} style={{ padding: '5px 11px', borderRadius: 100, border: `1px solid ${t.line}`, background: t.paperAlt, fontFamily: FONTS.sans, fontSize: 12, color: t.inkSoft }}>{chip}</span>
              ))}
            </div>
          ) : null;
        })()}

        {/* Interests */}
        {!!m.otherUser.activities?.length && (
          <div style={{ padding: '18px 20px 0' }}>
            <div style={{ fontFamily: FONTS.sans, fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 1.5, color: t.inkMute, fontWeight: 600, marginBottom: 10 }}>
              Into
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
              {m.otherUser.activities.map((a, i) => (
                <span key={i} style={{
                  padding: '6px 12px', borderRadius: 100,
                  border: `1px solid ${i === 0 ? t.terracotta : t.line}`,
                  background: i === 0 ? t.terracotta : t.paperAlt,
                  color: i === 0 ? t.paper : t.inkSoft,
                  fontFamily: FONTS.sans, fontSize: 12.5,
                }}>{a}</span>
              ))}
            </div>
          </div>
        )}

        {/* Spark answers */}
        {m.otherUser.sparkAnswers?.length > 0 && (
          <div style={{ padding: '16px 20px 40px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {m.otherUser.sparkAnswers.map((sa, i) => (
              <div key={i} style={{ background: t.card, borderRadius: 16, padding: '14px 16px', border: `1px solid ${t.line}` }}>
                <div style={{ fontFamily: FONTS.mono, fontSize: 10, color: t.inkMute, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 6 }}>
                  {sa.prompt}
                </div>
                <div style={{ fontFamily: FONTS.sans, fontSize: 14, color: t.ink, lineHeight: 1.5 }}>
                  {sa.answer}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
